import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');

  // Lead form data (without follow-up fields)
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date: new Date().toISOString().split('T')[0],
    source: 'Walk-In',
    notes: '',
    status: 'New'
  });

  // Follow-up form data
  const [followUpData, setFollowUpData] = useState({
    response: '',
    nextFollowUpDate: '',
    status: 'Contacted'
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterSource]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterSource !== 'all') params.append('source', filterSource);

      const [leadsRes, statsRes, sourcesRes] = await Promise.all([
        axios.get(`/api/leads?${params.toString()}`, { headers }),
        axios.get('/api/leads/stats', { headers }),
        axios.get('/api/leads/sources', { headers })
      ]);

      setLeads(leadsRes.data.leads || []);
      setStats(statsRes.data);
      setSources(sourcesRes.data.sources || []);
    } catch (error) {
      console.error('Failed to fetch leads data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    const token = localStorage.getItem('token');

    try {
      if (editingLead) {
        await axios.put(`/api/leads/${editingLead.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccessMessage('Lead updated successfully!');
      } else {
        await axios.post('/api/leads', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccessMessage('Lead created successfully!');
      }
      fetchData();
      closeModal();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save lead');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFollowUpSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !selectedLead) return;

    setIsSubmitting(true);
    const token = localStorage.getItem('token');

    try {
      await axios.post(`/api/leads/${selectedLead.id}/followup`, followUpData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMessage('Follow-up logged successfully!');
      fetchData();
      // Refresh follow-up history
      fetchFollowUpHistory(selectedLead.id);
      // Reset form but keep modal open to show updated history
      setFollowUpData({
        response: '',
        nextFollowUpDate: '',
        status: 'Contacted'
      });
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to log follow-up');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchFollowUpHistory = async (leadId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/leads/${leadId}/followups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowUpHistory(res.data.followups || []);
    } catch (error) {
      console.error('Failed to fetch follow-up history:', error);
      setFollowUpHistory([]);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMessage('Lead deleted successfully!');
      fetchData();
    } catch (error) {
      alert('Failed to delete lead');
    }
  };

  const openEditModal = (lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name || '',
      phone: lead.phone || '',
      email: lead.email || '',
      date: lead.leadDate ? new Date(lead.leadDate).toISOString().split('T')[0] : '',
      source: lead.source || 'Walk-In',
      notes: lead.notes || '',
      status: lead.status || 'New'
    });
    setShowModal(true);
  };

  const openFollowUpModal = async (lead) => {
    setSelectedLead(lead);
    setFollowUpData({
      response: '',
      nextFollowUpDate: '',
      status: lead.status === 'New' ? 'Contacted' : lead.status
    });
    await fetchFollowUpHistory(lead.id);
    setShowFollowUpModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingLead(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      date: new Date().toISOString().split('T')[0],
      source: 'Walk-In',
      notes: '',
      status: 'New'
    });
  };

  const closeFollowUpModal = () => {
    setShowFollowUpModal(false);
    setSelectedLead(null);
    setFollowUpHistory([]);
    setFollowUpData({
      response: '',
      nextFollowUpDate: '',
      status: 'Contacted'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return '#2196F3';
      case 'Contacted': return '#FF9800';
      case 'Interested': return '#4CAF50';
      case 'Not Interested': return '#f44336';
      case 'Converted': return '#9C27B0';
      default: return '#666';
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'Trial Member': return '🏋️';
      case 'Walk-In': return '🚶';
      case 'Email': return '📧';
      case 'Phone': return '📞';
      case 'Referral': return '👥';
      case 'Social Media': return '📱';
      default: return '📝';
    }
  };

  if (loading) return <div className="loading">Loading leads...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>🎯 Leads</h1>
        <button className="btn btn-success" onClick={() => setShowModal(true)}>
          + Add Lead
        </button>
      </div>

      {/* Success Message Popup */}
      {successMessage && (
        <div className="success-popup" style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#4CAF50',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '5px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          zIndex: 1000,
          animation: 'fadeIn 0.3s'
        }}>
          {successMessage}
          <button 
            onClick={() => setSuccessMessage('')}
            style={{
              marginLeft: '15px',
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >×</button>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid" style={{ marginBottom: '20px' }}>
          <div className="stat-card">
            <h3>Total Leads</h3>
            <p className="stat-value">{stats.total || 0}</p>
          </div>
          <div className="stat-card">
            <h3>New</h3>
            <p className="stat-value" style={{ color: '#2196F3' }}>{stats.new || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Contacted</h3>
            <p className="stat-value" style={{ color: '#FF9800' }}>{stats.contacted || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Interested</h3>
            <p className="stat-value" style={{ color: '#4CAF50' }}>{stats.interested || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Converted</h3>
            <p className="stat-value" style={{ color: '#9C27B0' }}>{stats.converted || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label style={{ marginRight: '10px' }}>Status:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '5px' }}
          >
            <option value="all">All Statuses</option>
            <option value="New">New</option>
            <option value="Contacted">Contacted</option>
            <option value="Interested">Interested</option>
            <option value="Not Interested">Not Interested</option>
            <option value="Converted">Converted</option>
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label style={{ marginRight: '10px' }}>Source:</label>
          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '5px' }}
          >
            <option value="all">All Sources</option>
            {sources.map(source => (
              <option key={source} value={source}>{source}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Leads Table */}
      <div className="table-container">
        {leads.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Date</th>
                <th>Source</th>
                <th>Next Follow-up</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => (
                <tr key={lead.id}>
                  <td><strong>{lead.name}</strong></td>
                  <td>{lead.phone || '-'}</td>
                  <td>{lead.email || '-'}</td>
                  <td>{formatDate(lead.leadDate)}</td>
                  <td>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {getSourceIcon(lead.source)} {lead.source}
                    </span>
                  </td>
                  <td>{formatDate(lead.nextFollowUpDate)}</td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      borderRadius: '15px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: 'white',
                      background: getStatusColor(lead.status)
                    }}>
                      {lead.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons" style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      <button 
                        className="action-btn" 
                        onClick={() => openFollowUpModal(lead)}
                        style={{ background: '#FF9800', color: 'white' }}
                      >
                        📞 Follow-up
                      </button>
                      <button className="action-btn edit" onClick={() => openEditModal(lead)}>
                        Edit
                      </button>
                      <button className="action-btn delete" onClick={() => handleDelete(lead.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <h3>No leads found</h3>
            <p>Add a new lead or adjust your filters</p>
          </div>
        )}
      </div>

      {/* Add/Edit Lead Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2>{editingLead ? 'Edit Lead' : 'Add New Lead'}</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Lead name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Phone number"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Email address"
                  />
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={e => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Source</label>
                  <select
                    value={formData.source}
                    onChange={e => setFormData({ ...formData, source: e.target.value })}
                  >
                    <option value="Trial Member">🏋️ Trial Member Payment</option>
                    <option value="Walk-In">🚶 Walk-In</option>
                    <option value="Email">📧 Email Enquiry</option>
                    <option value="Phone">📞 Phone Enquiry</option>
                    <option value="Referral">👥 Referral</option>
                    <option value="Social Media">📱 Social Media</option>
                    <option value="Other">📝 Other</option>
                  </select>
                </div>
                {editingLead && (
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={formData.status}
                      onChange={e => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="New">New</option>
                      <option value="Contacted">Contacted</option>
                      <option value="Interested">Interested</option>
                      <option value="Not Interested">Not Interested</option>
                      <option value="Converted">Converted</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="form-group" style={{ marginTop: '15px' }}>
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes about the lead..."
                  rows="2"
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : (editingLead ? 'Update Lead' : 'Add Lead')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Follow-up Modal */}
      {showFollowUpModal && selectedLead && (
        <div className="modal-overlay" onClick={closeFollowUpModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}>
            <h2>📞 Follow-up: {selectedLead.name}</h2>
            
            {/* Lead Info Summary */}
            <div style={{ 
              background: '#f5f5f5', 
              padding: '12px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px',
              fontSize: '14px'
            }}>
              <div><strong>Phone:</strong> {selectedLead.phone || '-'}</div>
              <div><strong>Email:</strong> {selectedLead.email || '-'}</div>
              <div><strong>Source:</strong> {getSourceIcon(selectedLead.source)} {selectedLead.source}</div>
              <div><strong>Status:</strong> 
                <span style={{
                  marginLeft: '5px',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  color: 'white',
                  background: getStatusColor(selectedLead.status)
                }}>
                  {selectedLead.status}
                </span>
              </div>
            </div>

            {/* Log New Follow-up Form */}
            <form onSubmit={handleFollowUpSubmit}>
              <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>Log New Follow-up</h3>
              <div className="form-group">
                <label>Response/Notes *</label>
                <textarea
                  value={followUpData.response}
                  onChange={e => setFollowUpData({ ...followUpData, response: e.target.value })}
                  placeholder="What was discussed? Any feedback from the lead?"
                  rows="3"
                  required
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label>Next Follow-up Date</label>
                  <input
                    type="date"
                    value={followUpData.nextFollowUpDate}
                    onChange={e => setFollowUpData({ ...followUpData, nextFollowUpDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Update Status</label>
                  <select
                    value={followUpData.status}
                    onChange={e => setFollowUpData({ ...followUpData, status: e.target.value })}
                  >
                    <option value="New">New</option>
                    <option value="Contacted">Contacted</option>
                    <option value="Interested">Interested</option>
                    <option value="Not Interested">Not Interested</option>
                    <option value="Converted">Converted</option>
                  </select>
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting} style={{ width: '100%', marginTop: '10px' }}>
                {isSubmitting ? 'Logging...' : '✓ Log Follow-up'}
              </button>
            </form>

            {/* Follow-up History */}
            <div style={{ marginTop: '25px' }}>
              <h3 style={{ marginBottom: '15px', fontSize: '16px', borderTop: '1px solid #ddd', paddingTop: '20px' }}>
                📋 Follow-up History ({followUpHistory.length})
              </h3>
              {followUpHistory.length > 0 ? (
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {followUpHistory.map((fu, index) => (
                    <div key={fu.id || index} style={{
                      background: index % 2 === 0 ? '#f9f9f9' : '#fff',
                      padding: '12px',
                      borderRadius: '8px',
                      marginBottom: '10px',
                      border: '1px solid #eee'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <strong style={{ color: '#333' }}>{fu.followedUpBy}</strong>
                        <span style={{ color: '#666', fontSize: '12px' }}>{formatDateTime(fu.followUpDate || fu.createdAt)}</span>
                      </div>
                      <p style={{ margin: '0 0 8px 0', color: '#555' }}>{fu.response || 'No notes'}</p>
                      <div style={{ display: 'flex', gap: '15px', fontSize: '12px', color: '#888' }}>
                        {fu.nextFollowUpDate && (
                          <span>📅 Next: {formatDate(fu.nextFollowUpDate)}</span>
                        )}
                        {fu.statusAfter && (
                          <span>
                            Status → 
                            <span style={{
                              marginLeft: '5px',
                              padding: '2px 6px',
                              borderRadius: '8px',
                              fontSize: '10px',
                              color: 'white',
                              background: getStatusColor(fu.statusAfter)
                            }}>
                              {fu.statusAfter}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>
                  No follow-ups logged yet
                </p>
              )}
            </div>

            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button type="button" className="btn btn-secondary" onClick={closeFollowUpModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
